import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { cn } from '../../utils/cn';

const PINEntryModal = ({ onClose, onSubmit, title, description, submitLabel = 'Unlock', showCancel = true }) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    // Focus first input on mount
    inputRefs?.[0]?.current?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/?.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs?.[index + 1]?.current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e?.key === 'Backspace' && !pin?.[index] && index > 0) {
      inputRefs?.[index - 1]?.current?.focus();
    }

    // Handle Enter key
    if (e?.key === 'Enter' && pin?.every(digit => digit !== '')) {
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pastedData = e?.clipboardData?.getData('text')?.slice(0, 4);
    
    if (/^\d+$/?.test(pastedData)) {
      const newPin = pastedData?.split('');
      while (newPin?.length < 4) newPin?.push('');
      setPin(newPin?.slice(0, 4));
      
      // Focus last filled input or first empty
      const focusIndex = Math.min(pastedData?.length, 3);
      inputRefs?.[focusIndex]?.current?.focus();
    }
  };

  const handleSubmit = async () => {
    const pinValue = pin?.join('');
    
    if (pinValue?.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await onSubmit(pinValue);
      
      if (result === false) {
        setError('Incorrect PIN. Please try again.');
        setPin(['', '', '', '']);
        inputRefs?.[0]?.current?.focus();
      }
    } catch (err) {
      setError(err?.message || 'An error occurred');
      setPin(['', '', '', '']);
      inputRefs?.[0]?.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[400] p-4">
      <div className="bg-popover border border-border rounded-lg shadow-warm-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="lock" size={20} className="text-primary" />
            </div>
            <h2 className="text-h3 font-heading text-foreground">
              {title || 'Enter PIN'}
            </h2>
          </div>
          {showCancel && (
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <Icon name="x" size={20} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {description && (
            <p className="text-sm text-muted-foreground text-center">
              {description}
            </p>
          )}

          {/* PIN Input */}
          <div className="flex justify-center gap-3">
            {pin?.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs?.[index]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e?.target?.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className={cn(
                  "w-14 h-14 text-center text-2xl font-bold rounded-lg border-2 transition-all",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                  error ? "border-destructive" : "border-input",
                  digit ? "bg-primary/5" : "bg-background"
                )}
                aria-label={`PIN digit ${index + 1}`}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <Icon name="alert-circle" size={16} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          {showCancel && (
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={pin?.some(digit => digit === '') || isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PINEntryModal;
